import React from 'react';
import * as yup from 'yup';
import { ClientApiProps, IAssessorApplicationEx, yupFileTestOptions, fileAcceptString, yupPhoneTestOptions } from '../../common';
import { AddressPicker, AddressSchema, Button, Checkbox, ErrorScope, FormContext, FormPage, Label, PhoneInput, RadioList, Select, TextInput } from 'acma-spa';
import { Acma_amateurradioassessor_statuscode, Acma_amateurradioassessortypes } from '../../api';
import { Link } from '../../common/FormHelpers';

export const schema: yup.ObjectSchema<IAssessorApplicationEx> = yup.object({
  id: yup.string(),
  referenceNumber: yup.string(),
  title: yup.string(),
  firstName: yup.string().required().label('First Name'),
  lastName: yup.string().required().label('Last Name'),
  emailAddress: yup.string().email().required().label('Email address'),
  phone1: yup.string().required().label('Contact phone').test(yupPhoneTestOptions),
  phone2: yup.string().label('Other phone').test(yupPhoneTestOptions),
  otherName: yup.string(),
  callsign: yup.string(),
  pastRejection: yup.boolean().required('Field is required'),
  advertised: yup.boolean().required('Field is required'),
  advertisedEmail: yup.string().email().optional().label('Email'),
  advertisedPhone: yup.string().optional().label('Phone').test(yupPhoneTestOptions),
  address1: AddressSchema.default(undefined).required('Address is required'),
  advertisedAddress: yup.object().default(undefined),
  type: yup.mixed<Acma_amateurradioassessortypes>().oneOf(Object.values(Acma_amateurradioassessortypes)).required().label('Type'),
  status: yup.mixed<Acma_amateurradioassessor_statuscode>().oneOf(Object.values(Acma_amateurradioassessor_statuscode)).optional(),
  confirmation: yup.boolean().required('Confirmation required').isTrue('Confirmation required'),
  confirmation_shareWithStates: yup.boolean().required('Confirmation required').isTrue('Confirmation required'),
  fileNames: yup.array().defined('At least one document is required').min(1, 'At least one document is required').test(yupFileTestOptions),
});

export interface AssessorApplicationPageProps extends ClientApiProps {
  ctx: FormContext<IAssessorApplicationEx>;
  onFileAdded: (files: File[]) => string[];
  onFileRemoved: (name: string) => string[];
}

export const AssessorApplicationPage: React.FC<AssessorApplicationPageProps> = ({ ctx, ...props }) => {
  return (
    <FormPage<IAssessorApplicationEx> ctx={ctx} schema={schema}>
      {(ctx) => {
        const { model, setModel, errors } = ctx;

        const infoMessage = (
          <>
            <p>
              The ACMA is collecting the personal information contained in this application for accreditation as either a General Assessor Accreditation or a
              Specialist Assessor Accreditation consistent with the{' '}
              {Link(props.contentUrls.examRules, 'Radiocommunications Accreditation (Amateur Radio Examinations) Rules 2023')} and the ACMA&apos;s and the
              {Link(props.contentUrls.callSignPolicy, ' ACMA’s Accredited Assessor Guidelines')}.
            </p>
            <p>
              As part of the application, the ACMA may disclose your personal information to the relevant Australian State or Territory that you have obtained a
              Working with Children or Vulnerable People Check Clearance (WWCVPCC). The purpose of disclosure is to verify the status of your WWCVPCC and to
              inform the Australian State or Territory agency who administers the WWCVPC that you will be volunteering as an accredited assessor at the ACMA.
            </p>
            <p>
              If you are unable to obtain a WWCVPCC, the ACMA may require you to provide a nationally coordinated criminal history check. We will not disclose
              details of this check to anybody and it will not be used for anything other than to enable the ACMA to assess your application to become an
              accredited assessor at the ACMA.
            </p>
            <p>
              If you are unable to provide details of your WWCVPCC or a nationally coordinated criminal history check we will be unable to process your
              application to become an accredited person.
            </p>
            <p>
              The ACMA will not otherwise use the personal information in this form for any other purpose, nor will we disclose it, unless we have your consent
              or we are otherwise permitted to do so under the Privacy Act 1988 (the Privacy Act).
            </p>
            <p>
              Further information on the Privacy Act and the ACMA’s Privacy Policy is available at {Link(props.contentUrls.privacyPolicy)}. The ACMA’s Privacy
              Policy contains details about how you may access personal information about you that is held by the ACMA, and seek the correction of such
              information. It also explains how you may complain about a breach of the Privacy Act and how we will deal with such a complaint.
            </p>
          </>
        );

        return (
          <>
            <div className="alert alert-info">{infoMessage}</div>

            <p>Complete this form and upload the following supporting documents at the bottom of the page:</p>
            <ul>
              <li>
                a copy of your Amateur Radio Operator’s Certificate of Proficiency (Advanced), ACMA recognition certificate (Advanced) or the letter of confirmation (Advanced)
                you received from the ACMA
              </li>
              <li>
                for applicants in all states and territories <i>except NSW:</i> a copy of your Working with Children or Vulnerable People Check Clearance (WWCVPCC) from the state or territory you will be conducting and assessing amateur examinations
              </li>
              <li>
                for <i>NSW applicants:</i> if you do not have a WWCVPCC, a copy of a Nationally Coordinated Criminal History Check you have completed in NSW issued no more than 30 days before your application.
              </li>
            </ul>
            <p>
              <b>After you apply</b>
            </p>
            <ul>
              <li>
                Once you submit this form, you’ll receive a confirmation email. If you don’t receive a confirmation email, please contact{' '}
                <a href="mailto:arassessors@acma.gov.au">arassessors@acma.gov.au</a>.
              </li>
              <li>
                Your application will be processed consistent with the{' '}
                {Link(props.contentUrls.examRules, 'Radiocommunications Accreditation (Amateur Radio Examination) Rules 2023')}
                and the ACMA's <i>{Link(props.contentUrls.callSignPolicy, 'Accredited Assessor Guidelines')}</i>.
              </li>
              <li>We aim to contact you within 10 business days of receiving your application.</li>
              <li>
                We will contact you by email if we require any further information from you. If you have any questions, please email{' '}
                <a href="mailto:arassessors@acma.gov.au">arassessors@acma.gov.au</a>.
              </li>
            </ul>

            <div className="row">
              <div className="col-sm-4 mb-3">
                <Label id="title" label="Title" error={errors?.title}>
                  <TextInput readonly={ctx.readOnly} value={model.title || ''} onChange={(v) => setModel({ title: v })} maxLength={400} />
                </Label>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-4 mb-3">
                <Label id="firstName" label="First name" error={errors?.firstName} required={true}>
                  <TextInput readonly={ctx.readOnly} value={model.firstName || ''} onChange={(v) => setModel({ firstName: v })} maxLength={149} />
                </Label>
              </div>
              <div className="col-sm-4 mb-3">
                <Label id="lastName" label="Last name" error={errors?.lastName} required={true}>
                  <TextInput readonly={ctx.readOnly} value={model.lastName || ''} onChange={(v) => setModel({ lastName: v })} maxLength={149} />
                </Label>
              </div>
              <div className="col-sm-4 mb-3">
                <Label id="otherName" label="Other name" error={errors?.otherName}>
                  <TextInput readonly={ctx.readOnly} value={model.otherName || ''} onChange={(v) => setModel({ otherName: v })} maxLength={149} />
                </Label>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-4 mb-3">
                <Label id="emailAddress" label="Email address" error={errors?.emailAddress} required={true}>
                  <TextInput readonly={ctx.readOnly} value={model.emailAddress || ''} onChange={(v) => setModel({ emailAddress: v })} maxLength={400} />
                </Label>
              </div>
              <div className="col-sm-4 mb-3">
                <Label id="phone1" label="Contact phone" error={errors?.phone1} required={true}>
                  <PhoneInput readonly={ctx.readOnly} value={model.phone1 || ''} onChange={(v) => setModel({ phone1: v })} maxLength={100} />
                </Label>
              </div>
              <div className="col-sm-4 mb-3">
                <Label id="phone2" label="Other phone" error={errors?.phone2}>
                  <PhoneInput readonly={ctx.readOnly} value={model.phone2 || ''} onChange={(v) => setModel({ phone2: v })} maxLength={100} />
                </Label>
              </div>
            </div>
            <div className="row">
              <div className="col mb-3">
                <Label id="address1" label="Contact address" error={errors?.address1} required={true}>
                  <AddressPicker value={model.address1 || null} onChange={(address) => setModel({ address1: address || undefined })} disabled={ctx.readOnly} />
                </Label>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-4 mb-3">
                <Label id="type" label="Assessor type" error={errors?.type} required={true}>
                  <Select<Acma_amateurradioassessortypes>
                    options={[
                      { label: 'General accredited assessor', value: Acma_amateurradioassessortypes.Standard },
                      { label: 'Specialist accredited assessor', value: Acma_amateurradioassessortypes.Specialist },
                    ]}
                    value={model.type || null}
                    onChange={(type) => setModel({ type: type || undefined })}
                    defaultLabel="Select..."
                    size="block"
                    disabled={ctx.readOnly}
                  />
                </Label>
              </div>
              <div className="col-sm-4 mb-3">
                <Label id="callsign" label="Call sign" error={errors?.callsign}>
                  <TextInput readonly={ctx.readOnly} value={model.callsign || ''} onChange={(v) => setModel({ callsign: v })} maxLength={400} />
                </Label>
              </div>
            </div>

            <div className="row">
              <div className="col mb-3">
                <Label
                  id="pastRejection"
                  label="Has the Australian Maritime College (AMC) or the ACMA withdrawn your assessor accreditation in the last 5 years?"
                  error={errors?.pastRejection}
                  required>
                  <RadioList<boolean | undefined>
                    id="pastRejection"
                    value={model.pastRejection}
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                    onChange={(pastRejection) => {
                      setModel({ pastRejection });
                    }}
                    disabled={ctx.readOnly}
                  />
                </Label>
              </div>
            </div>

            <div className="row">
              <div className="col mb-3">
                <Label
                  id="advertised"
                  label="Do you consent to your personal information being published on the list of General and Specialist accredited assessors on the ACMA website? You can choose the information you would like to appear."
                  error={errors?.advertised}
                  required>
                  <RadioList<boolean | undefined>
                    id="advertised"
                    value={model.advertised}
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                    onChange={(advertised) => {
                      setModel({ advertised });
                    }}
                    disabled={ctx.readOnly}
                  />
                </Label>
              </div>
            </div>
            {model.advertised && (
              <>
                <div className="row">
                  <div className="col mb-3">
                    <p>
                      You have indicated that you would like to have your personal details such as your name, email address, phone number and postcode published
                      on the ACMA’s website and to identify you as either a General or Specialist Accredited Assessor (ACMA public website list). Note that the
                      ACMA public website list is a separate and different list to the List of Authorised Assessors referred to in the{' '}
                      <i>Radiocommunications Accreditation (Amateur Radio Examinations) Rules 2023</i> (Rules). The personal information on the ACMA public
                      website list will be accessible to the public on the ACMA’s website and you will be contactable by a candidate, who may wish to have
                      arranged an amateur radio examination. You can choose which of your personal information is made available on the ACMA public website
                      list. If you do not want to have any of your personal information published on the ACMA public website list, you are still able to make a
                      valid application to become an ACMA Accredited General or Specialist Assessor.
                    </p>
                    <p>
                      Further information on the <i>Privacy Act 1988</i> (the Privacy Act) and the ACMA's Privacy Policy is available at{' '}
                      {Link(props.contentUrls.privacyPolicy)}. The Privacy Policy contains details about how you may access personal information about you that
                      is held by the ACMA, and seek the correction of such information. It also explains how you may complain about a breach of the{' '}
                      {Link(props.contentUrls.privacyAct, 'Privacy Act')} and how we will deal with such a complaint.
                    </p>
                  </div>
                </div>{' '}
              </>
            )}
            <div className="row">
              <div className="col mb-3" style={{ display: model.advertised == true ? '' : 'none' }}>
                <Label id="advertisedEmail" label="Enter your email address to display on the public register" error={errors?.advertisedEmail}>
                  <TextInput
                    id="advertisedEmail"
                    readonly={ctx.readOnly}
                    value={model.advertisedEmail || ''}
                    onChange={(v) => setModel({ advertisedEmail: v })}
                    maxLength={400}
                  />
                </Label>
              </div>
              <div className="col mb-3" style={{ display: model.advertised == true ? '' : 'none' }}>
                <Label id="advertisedPhone" label="Enter your phone number to display on the public register" error={errors?.advertisedPhone}>
                  <PhoneInput
                    id="advertisedPhone"
                    readonly={ctx.readOnly}
                    value={model.advertisedPhone || ''}
                    onChange={(v) => setModel({ advertisedPhone: v })}
                    maxLength={100}
                  />
                </Label>
              </div>
            </div>

            <div className="row" style={{ display: model.advertised == true ? '' : 'none' }}>
              <div className="col mb-3">
                <Label
                  id="advertisedAddress"
                  label="Provide the address information you want to display on the public register – you can just display your suburb or state"
                  error={errors?.advertisedAddress}>
                  <AddressPicker
                    value={model.advertisedAddress || null}
                    onChange={(address) => setModel({ advertisedAddress: address || undefined })}
                    disabled={ctx.readOnly}
                  />
                </Label>
              </div>
            </div>

            <h2 className="mb-2">
              Supporting documents{' '}
              <span style={{ color: 'red', display: 'inline' }} aria-hidden="true">
                *
              </span>
            </h2>
            <p>Upload:</p>
            <ol>
              <li>
                a copy of your Amateur Radio Operator’s Certificate of Proficiency (Advanced), ACMA recognition certificate (Advanced) or the letter of confirmation (Advanced)
                you received from the ACMA
              </li>
              <li>
                for applicants in all states and territories except NSW a copy of your Working with Children or Vulnerable People Check Clearance (WWCVPCC) from the state or territory you will be conducting and assessing amateur examinations
              </li>
              <li>
                for NSW applicants: if you do not have a WWCVPCC, a copy of a Nationally Coordinated Criminal History Check you have completed in NSW issued no more than 30 days before your application.
              </li>
              {model.type == Acma_amateurradioassessortypes.Specialist ? (
                <li>proof of a formal teaching qualification (eg Diploma, Masters, Bachelors) or Certificate IV in Training and Assessment</li>
              ) : (
                <></>
              )}
            </ol>
            <ErrorScope id="fileNames" error={errors.fileNames}>
              {model.fileNames && model.fileNames.length > 0 ? (
                <Label id="files" label="Uploaded files">
                  <ul className="list-unstyled mt-2">
                    {model.fileNames.map((r, i) => (
                      <li key={i}>
                        {r}{' '}
                        <a
                          href="#"
                          title="Remove"
                          style={{ display: ctx.readOnly ? 'none' : '' }}
                          onClick={(e) => {
                            e.preventDefault();
                            const fileNames = props.onFileRemoved(r);
                            setModel({ fileNames });
                          }}>
                          X
                        </a>
                      </li>
                    ))}
                  </ul>
                </Label>
              ) : (
                <></>
              )}
              {ctx.readOnly ? (
                <></>
              ) : (
                <Button id="fileNames" label="Upload documents" onClick={() => document.getElementById('assessor-document-upload')?.click()} />
              )}
            </ErrorScope>
            <input
              type="file"
              id="assessor-document-upload"
              style={{ display: 'none' }}
              multiple={true}
              accept={fileAcceptString}
              onChange={(e) => {
                const fileNames = props.onFileAdded(e.target.files ? Array.from(e.target.files) : []);
                setModel({ fileNames });
                e.target.value = '';
              }}
            />

            <hr />

            <div className="row">
              <div className="col mb-3">
                <ErrorScope id="confirmation" error={errors?.confirmation}>
                  <Checkbox
                    id="confirmation"
                    label="I confirm that the information provided is true and correct."
                    checked={ctx.readOnly ? true : model.confirmation ?? false}
                    onChange={(v) => setModel({ confirmation: v })}
                    disabled={ctx.readOnly}
                    required
                  />
                </ErrorScope>
              </div>
            </div>

            {/* TODO: This text and logic needs updating. */}
            <div className="row">
              <div className="col mb-3">
                <ErrorScope id="confirmation_shareWithStates" error={errors?.confirmation_shareWithStates}>
                  <Checkbox
                    id="confirmation_shareWithStates"
                    label="I allow my information to be shared with states and territories for the Working with Children or Working with Vulnerable People Check."
                    checked={ctx.readOnly ? true : model.confirmation_shareWithStates ?? false}
                    onChange={(v) => setModel({ confirmation_shareWithStates: v })}
                    disabled={ctx.readOnly}
                    required
                  />
                </ErrorScope>
              </div>
            </div>
          </>
        );
      }}
    </FormPage>
  );
};
